import { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useCreateEntry, useDeleteEntry, useStartTimer, useUpdateEntry } from '@/hooks/useEntries';
import { useActivities, useClients, useCreateActivity, useCreateClient } from '@/hooks/useReference';
import { useBillingNouns, useSession } from '@/hooks/useSession';
import { formatDay, hoursShort, parseHours } from '@/lib/format';
import type { TimeEntry } from '@/types';
import { Sheet, SheetBack } from '@m/ui/Sheet';
import { PickerList } from '@m/ui/Picker';
import { ColorDot, Group } from '@m/ui/List';
import { BarButton, Button, HoursInput } from '@m/ui/Controls';
import { useWeekEntries } from './time/week';

/**
 * New or edit time entry (S03), and the start-a-timer form (`mode: 'timer'`,
 * which is the same form without hours).
 *
 * Client and Activity push a picker panel inside this same sheet (S04): the
 * header becomes "‹ New entry / Client", the body the searchable list with
 * "Add new" first and the client's colour dot.
 *
 * The canvas also shows a "Billable" switch. Time entries have no billable
 * field on the server, so it is left out rather than shown doing nothing.
 */
export function EntrySheet({
  open,
  entry,
  defaultDate,
  defaultClientId,
  mode: initialMode = 'entry',
  onClose
}: {
  open: boolean;
  entry: TimeEntry | null;
  defaultDate: string;
  defaultClientId?: string;
  mode?: 'entry' | 'timer';
  onClose: () => void;
}) {
  const session = useSession();
  const nouns = useBillingNouns();
  const clients = useClients().data ?? [];
  const activities = useActivities().data ?? [];
  const createClient = useCreateClient();
  const createActivity = useCreateActivity();
  const create = useCreateEntry(session.user);
  const update = useUpdateEntry();
  const del = useDeleteEntry();
  const startTimer = useStartTimer();
  const week = useWeekEntries(defaultDate).data?.items;

  const [mode, setMode] = useState(entry ? 'entry' : initialMode);
  const [date, setDate] = useState(entry?.date ?? defaultDate);
  const [clientId, setClientId] = useState(entry?.clientId ?? defaultClientId ?? null);
  const [activityId, setActivityId] = useState(entry?.activityId ?? null);
  const [hoursText, setHoursText] = useState(entry ? hoursShort(entry.time) : '');
  const [note, setNote] = useState(entry?.misc ?? '');
  const [picking, setPicking] = useState<'client' | 'activity' | null>(null);

  const hours = parseHours(hoursText);
  const timer = mode === 'timer';
  const valid = !!clientId && !!activityId && (timer || (hours !== null && hours > 0));
  const dirty = entry
    ? date !== entry.date ||
      clientId !== entry.clientId ||
      activityId !== entry.activityId ||
      hours !== entry.time ||
      (note || null) !== (entry.misc || null)
    : Boolean(hoursText || note || activityId || (clientId && clientId !== defaultClientId));

  const activeClients = clients.filter((c) => c.status === 'active');
  // The three clients worked on most recently this week.
  const recentClients = useMemo(() => {
    const seen: string[] = [];
    for (const e of [...(week ?? [])].sort((a, b) => (a.date < b.date ? 1 : -1))) {
      if (!seen.includes(e.clientId)) seen.push(e.clientId);
      if (seen.length === 3) break;
    }
    return seen.map((id) => activeClients.find((c) => c.id === id)).filter((c): c is NonNullable<typeof c> => !!c);
  }, [week, activeClients]);

  const client = clients.find((c) => c.id === clientId);
  const activity = activities.find((a) => a.id === activityId);
  const title = entry ? 'Edit entry' : timer ? 'Start timer' : 'New entry';

  const save = () => {
    if (!valid) return;
    if (timer) {
      startTimer.mutate({ clientId: clientId!, activityId: activityId!, note: note || undefined });
    } else if (entry) {
      update.mutate({ id: entry.id, patch: { date, clientId: clientId!, activityId: activityId!, time: hours!, misc: note || null } });
    } else {
      create.mutate({ date, clientId: clientId!, activityId: activityId!, time: hours!, misc: note || null });
    }
    onClose();
  };

  if (picking) {
    const back = <SheetBack label={title} onClick={() => setPicking(null)} />;
    return (
      <Sheet open={open} onClose={onClose} full dirty={dirty} title={picking === 'client' ? nouns.client : 'Activity'} leading={back}>
        {picking === 'client' ? (
          <PickerList
            items={activeClients}
            recent={recentClients}
            value={clientId}
            noun={nouns.clientLower}
            leading={(c) => <ColorDot id={c.id} />}
            onSelect={(c) => {
              setClientId(c.id);
              setPicking(null);
            }}
            onCreate={(name) => createClient.mutateAsync({ name })}
          />
        ) : (
          <PickerList
            items={activities}
            value={activityId}
            noun="activity"
            nounPlural="activities"
            onSelect={(a) => {
              setActivityId(a.id);
              setPicking(null);
            }}
            onCreate={(name) => createActivity.mutateAsync({ name })}
          />
        )}
      </Sheet>
    );
  }

  const pickRow = (label: string, value: React.ReactNode, onClick: () => void) => (
    <button type="button" onClick={onClick} className="flex h-[52px] w-full items-center gap-3 px-4 text-left active:bg-[var(--pressed)]">
      <span className="flex-1">{label}</span>
      <span className="flex min-w-0 items-center gap-2 truncate text-[var(--text-muted)]">{value}</span>
      <ChevronRight className="size-4 shrink-0 text-[var(--text-subtle)]" strokeWidth={2.4} />
    </button>
  );

  return (
    <Sheet
      open={open}
      onClose={onClose}
      full
      dirty={dirty}
      title={title}
      leading={<BarButton onClick={onClose}>Cancel</BarButton>}
      trailing={
        <BarButton className="font-semibold" disabled={!valid} onClick={save}>
          {timer ? 'Start' : 'Save'}
        </BarButton>
      }
      footer={
        <div className="flex flex-col gap-1.5 px-4 pb-4">
          <Button disabled={!valid} onClick={save}>
            {timer ? 'Start timer' : 'Save entry'}
          </Button>
          {!entry && (
            <Button variant="plain" onClick={() => setMode(timer ? 'entry' : 'timer')}>
              {timer ? 'Add hours instead' : 'Start a timer instead'}
            </Button>
          )}
          {entry && (
            <Button
              variant="plain"
              className="text-[var(--danger)]"
              onClick={() => {
                del.mutate({ id: entry.id });
                onClose();
              }}
            >
              Delete entry
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {!timer && <HoursInput value={hoursText} onChange={setHoursText} />}

        <Group>
          {pickRow(
            nouns.client,
            client ? (
              <>
                <ColorDot id={client.id} />
                <span className="truncate">{client.name}</span>
              </>
            ) : (
              'Choose'
            ),
            () => setPicking('client')
          )}
          {pickRow('Activity', activity?.name ?? 'Choose', () => setPicking('activity'))}
          {!timer && (
            <label className="relative flex h-[52px] items-center gap-3 px-4">
              <span className="flex-1">Date</span>
              <span className="inline-flex h-[34px] items-center rounded-lg bg-[var(--page)] px-3">{formatDay(date)}</span>
              <input
                type="date"
                aria-label="Date"
                value={date}
                onChange={(e) => e.target.value && setDate(e.target.value)}
                className="absolute inset-0 opacity-0"
              />
            </label>
          )}
        </Group>

        <label className="flex flex-col gap-1.5 rounded-2xl bg-[var(--surface)] px-4 py-3">
          <span className="text-caption font-medium text-[var(--text-muted)]">Notes</span>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What did you do?"
            className="resize-none bg-transparent text-body leading-6 outline-none placeholder:text-[var(--text-subtle)]"
          />
        </label>
      </div>
    </Sheet>
  );
}
